<template>
    <Card
        :size="`col-md-10 col-lg-8 col-xl-6 max-xl-670`"
        :newLayout="true"
        :hideFooter="false"
    >
        <template slot="card-header">
            <h1 v-translate>Sign up to SmartCharts</h1>
            <EnrolmentProgress :currentStage="$options.name" />
        </template>
        <template slot="card-body">
            <h3
                class="text-center"
                v-translate
            >Review your subscription plan</h3>
            <template v-if="Object.keys(subscriptions).length">
                <div class="row justify-content-center mt-4">
                    <div class="col-md-11">
                        <div class="subscription">
                            <div class="row justify-content-center">
                                <div class="col-md-3 d-flex flex-column align-items-center">
                                    <div class="subscription__image">
                                        <img
                                            :src="subscriptions.Subscription_Image_URL__c"
                                            v-if="subscriptions.Subscription_Image_URL__c"
                                            alt=""
                                        />
                                        <svg-rndr
                                            svg="subscription-example.svg"
                                            v-else
                                        />
                                    </div>
                                    <h4 class="subscription__name">{{ subscriptions.Name }}</h4>
                                </div>
                                <div class="col-auto">
                                    <ul class="subscription__details">
                                        <li
                                            v-for="(item, ind) in formattedDescription"
                                            :key="ind"
                                        >
                                            {{ item }}
                                        </li>
                                    </ul>
                                    <a
                                        class="link subscription__video-link"
                                        @click="initVideoModal"
                                        :href="subscriptions.Subscription_Video_URL__c"
                                        v-if="subscriptions.Subscription_Video_URL__c"
                                        v-translate
                                    >See it in action</a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <form
                    class="enrol"
                    ref="form"
                    method="POST"
                    :action="$laraRoute('enrol')"
                    @submit.prevent="handleForm"
                    aria-label="Reset Password"
                    id="enrol-form"
                    novalidate
                >
                    <div class="row justify-content-center">
                        <div class="col-md-10">
                            <div class="form-group form-row justify-content-center">
                                <div
                                    class="enrol__item"
                                    v-for="(subscription, key) in subscriptions.pricing"
                                    :key="key"
                                >
                                    <div class="price d-flex flex-column align-items-center">
                                        <div class="price__input">
                                            <input
                                                :id="`subscription${subscription.Id}`"
                                                type="radio"
                                                class="form-control"
                                                name="subscription"
                                                required
                                                @change="purchaseOptionHandler"
                                                :value="subscription.Id"
                                            >
                                            <label
                                                :for="`subscription${subscription.Id}`"
                                                :aria-label="subscription.Name"
                                            ></label>
                                        </div>
                                        <span
                                            tabindex="-1"
                                            class="price__label-content"
                                        >{{ subscription.Name }}</span>
                                        <div
                                            class="price__values price__values--discounted"
                                            v-if="subscription.discount"
                                        >
                                            <div class="price__discounted">${{ subscription.Price__c }}</div>
                                            <div class="price__value">${{ subscription.Sale_Price__c }}</div>
                                        </div>
                                        <div
                                            class="price__values"
                                            v-else
                                        >
                                            <div class="price__value">${{ formatPrice(subscription.Price__c) }}</div>
                                        </div>
                                        <small
                                            class="price__has-discount"
                                            v-if="subscription.discount"
                                        >{{ subscription.discount }}% <translate>discount</translate></small>
                                    </div>
                                </div>
                            </div>
                            <div class="row justify-content-center mt-4">
                                <div class="col-md-8 text-center">
                                    <p
                                        class="subscription__first-payment"
                                        v-if="selectedSubscriptionBody"
                                    >
                                        {{ selectedSubscriptionBody }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </template>
            <template v-else>
                <div class="row justify-content-center mt-4">
                    <div class="col-md-8 col-lg-7 loader loader--inline">
                        <loader />
                    </div>
                </div>
            </template>
        </template>
        <template slot='card-footer'>
            <div class="row justify-content-center">
                <div class="col-md-8">
                    <flash-message></flash-message>
                </div>
            </div>

            <EnrolmentFooter
                :submitHandler="submitForm"
                :processStage="processStage"
            />
        </template>
        <template slot="card-extra">
            <modal
                name="subscription-video"
                width="100%"
                height="auto"
                v-show="Object.keys(subscriptions).length"
            >
                <VimeoPlayer
                    :videoId="videoId"
                    v-if="videoId"
                />
                <span
                    class="modal-close"
                    @click="hideModal"
                >
                    <svg-rndr svg="close.svg" />
                </span>
            </modal>
        </template>
    </Card>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import Card from '@C/Card';
import VimeoPlayer from '@C/VimeoPlayer';
import EnrolmentFooter from './Footer';
import EnrolmentMixin from '@M/enrolment.js';
import EnrolmentProgress from '@C/EnrolmentProgress';
import errorsMixin from '@M/errors';
import validator from '@M/validationWrapper';

export default {
    name: 'Subscription',
    beforeMount() {
        this.loadSubscriptions();
    },
    data() {
        return {
            videoId: null
        };
    },
    computed: {
        ...mapState('Enrolment', ['subscriptions']),
        ...mapGetters('Enrolment', ['getPurchaseOption']),
        selectedSubscriptionBody() {
            let purchaseOption = this.getPurchaseOption();

            if (
                typeof purchaseOption == 'undefined' ||
                purchaseOption === false ||
                parseFloat(purchaseOption.Price__c) === 0
            ) {
                return false;
            }

            let translated = this.$gettext(
                'First payment of $%{ price } for the subscription will be made after completing the enrolment.'
            );
            if (purchaseOption.Purchase_Option_Number__c === 0) {
                translated = this.$gettext(
                    'A one-off payment of $%{ price } for the subscription will be made after completing the enrolment.'
                );
            }

            return this.$gettextInterpolate(translated, {
                price: purchaseOption.Sale_Price__c
                    ? purchaseOption.Sale_Price__c
                    : purchaseOption.Price__c
            });
        },
        formattedDescription() {
            let description = this.subscriptions.Subscription_Description__c;

            return description ? description.split('\n').map(item => item) : '';
        }
    },
    methods: {
        ...mapActions('Enrolment', ['loadSubscriptions', 'setPurchaseOption']),
        purchaseOptionHandler(e) {
            if (typeof e === 'string') {
                this.setPurchaseOption(e);
            } else {
                this.setPurchaseOption(e.target.value);
            }
        },
        setupRequestData: function (request) {
            request.enrolment_purchase_subscription = this.subscriptions.subscription;
            request.enrolment_purchase_option = this.user.profile.enrolment_purchase_option;

            return request;
        },
        initVideoModal(e) {
            e.preventDefault();
            let hrefSplit = e.target.href.split('/');
            let videoId = hrefSplit[hrefSplit.length - 1];
            this.videoId = videoId;
            this.showModal();
        },
        showModal() {
            this.$modal.show('subscription-video');
        },
        hideModal() {
            this.$modal.hide('subscription-video');
        },
        formHandler(e) {
            this.processStage(e);
        },
        formatPrice(e) {
            return e.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        }
    },
    watch: {
        subscriptions(val) {
            if (!val) return;

            this.$nextTick(() => {
                let inputs = document.querySelectorAll('.price__input input');

                let selectedProductId;

                if (!this.user.profile.enrolment_purchase_option) {
                    // User hasn't selected a pricing option yet so check the first one
                    inputs[0].setAttribute('checked', true);
                    selectedProductId = inputs[0].value;
                } else {
                    // Otherwise select the input that correlates to their last selection
                    inputs.forEach(input => {
                        if (
                            input.value !=
                            this.user.profile.enrolment_purchase_option
                        )
                            return;

                        selectedProductId = input.value;
                        input.setAttribute('checked', true);
                    });
                }

                this.purchaseOptionHandler(selectedProductId);
            });
        }
    },
    components: {
        Card,
        VimeoPlayer,
        EnrolmentFooter,
        EnrolmentProgress
    },
    mixins: [EnrolmentMixin, errorsMixin, validator]
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/stages/Enrolment/Subscription.vue